import { FC, Fragment } from 'react';
import { ArrowLeftOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Row, Table, Typography } from 'antd';
import { useApp } from '@/App';
import FleetTitle from '@/libs/antd/components/Title';
import { useUserData } from '@/libs/fleet-api/hooks';
import { useMonitoringCovenantCustomerDetail } from '@/libs/fleet-api/services/monitoring';
import { useModals } from '@/components/modals/ModalProvider';

const MonitoringCovenantCustomerPage: FC<{
	customerId: string;
}> = ({ customerId }) => {
	const { navigate } = useApp();
	const { openCustomerGroupDetail, openCustomerDetail } = useModals();
	const userData = useUserData();

	const { data, isLoading, error } =
		useMonitoringCovenantCustomerDetail(customerId);

	if (isLoading || !userData) {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '200px',
				}}
			>
				<LoadingOutlined
					style={{ fontSize: 24 }}
					spin
				/>
			</div>
		);
	}

	if (error) {
		return (
			<div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
				Gagal memuat data customer.
			</div>
		);
	}

	if (!data) {
		return (
			<div style={{ textAlign: 'center', padding: '20px' }}>
				Data tidak ditemukan.
			</div>
		);
	}

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat('id-ID', {
			style: 'currency',
			currency: 'IDR',
			minimumFractionDigits: 0,
		}).format(amount);
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('id-ID', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		});
	};

	return (
		<Fragment>
			{/* Header */}
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 40,
				}}
			>
				<button
					style={{
						display: 'flex',
						height: 44,
						width: 44,
						fontSize: 24,
						border: 'none',
						alignItems: 'center',
						justifyContent: 'center',
						background: '#1557BC',
						color: 'white',
						cursor: 'pointer',
					}}
					onClick={() => navigate('MonitoringCovenantListPage')}
				>
					<ArrowLeftOutlined />
				</button>
				<div style={{ flex: 1 }}>
					<FleetTitle style={{ margin: 0 }}>
						List Monitoring Covenant
					</FleetTitle>
				</div>
			</div>

			{/* Customer Info */}
			<Row gutter={20}>
				<Col span={12}>
					<Row>
						<Col span={12}>
							<Typography.Text strong>Nama Customer</Typography.Text>
						</Col>
						<Col span={12}>
							<Typography.Text>
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();

										openCustomerDetail(data.customerId);
									}}
								>
									{data.customerName ?? '-'}
								</a>
							</Typography.Text>
						</Col>
					</Row>
				</Col>
				<Col span={12}>
					<Row>
						<Col span={12}>
							<Typography.Text strong>Group BMPP</Typography.Text>
						</Col>

						<Col span={12}>
							<Typography.Text>
								{data.bmppGroupId ? (
									<a
										href="#"
										className="trigger-popup"
										onClick={(e) => {
											e.preventDefault();

											openCustomerGroupDetail(data.bmppGroupId);
										}}
									>
										{data.bmppGroupName ?? '-'}
									</a>
								) : (
									<>{data.bmppGroupName ?? '-'}</>
								)}
							</Typography.Text>
						</Col>
					</Row>
				</Col>
			</Row>

			<Divider />

			<Table
				style={{ marginBlock: 20 }}
				bordered
				scroll={{ x: 'max-content' }}
				dataSource={data.spfList?.map((record, key) => ({
					...record,
					key,
				}))}
				pagination={false}
				columns={
					userData?.groupName === 'SECOM'
						? [
								{
									title: 'No',
									align: 'center',
									fixed: 'left',
									width: 60,
									render: (_, __, index) => index + 1,
								},
								{
									title: 'No.SPF',
									render: (record) => {
										return (
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													if (record.spfId && record.resolutionId) {
														navigate('SPFPage', {
															spfId: record.spfId,
															napResolutionId: record.resolutionId,
															onBack: () =>
																navigate('MonitoringCovenantListPage'),
															viewOnly: true,
														});
													}
												}}
											>
												{record.spfNo ?? '-'}
											</a>
										);
									},
								},
								{
									title: 'FRM',
									dataIndex: 'frmName',
									render: (value) => value ?? '-',
								},
								{
									title: 'FBDH',
									dataIndex: 'fbdhName',
									render: (value) => value ?? '-',
								},
								{
									title: 'Divisi',
									dataIndex: 'division',
									render: (value) => value ?? '-',
								},
								{
									title: 'Jenis Plafon',
									dataIndex: 'plafonType',
									render: (value) => value ?? '-',
								},
								{
									title: 'Sumber Order',
									dataIndex: 'orderSource',
									render: (value) => value ?? '-',
								},
								{
									title: 'Status Plafon',
									dataIndex: 'plafondStatus',
									render: (value) =>
										value === 'ACTIVE' ? 'Aktif' : 'Tidak Aktif',
								},
								{
									title: 'Detail Covenant',
									fixed: 'right',
									align: 'center',
									width: 120,
									render: (_, record) => (
										<Button
											type="link"
											onClick={() =>
												navigate('MonitoringCovenantPage', {
													resolutionId: record.resolutionId,
													spfId: record.spfId ?? undefined,
												})
											}
										>
											Detail
										</Button>
									),
								},
							]
						: [
								{
									title: 'No',
									align: 'center',
									fixed: 'left',
									width: 60,
									render: (_, __, index) => index + 1,
								},
								{
									title: 'No.SPF',
									render: (record) => {
										return (
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													if (record.spfId && record.resolutionId) {
														navigate('SPFPage', {
															spfId: record.spfId,
															napResolutionId: record.resolutionId,
															onBack: () =>
																navigate('MonitoringCovenantListPage'),
															viewOnly: true,
														});
													}
												}}
											>
												{record.spfNo ?? '-'}
											</a>
										);
									},
								},
								{
									title: 'FBDH',
									dataIndex: 'fbdhName',
									render: (value) => value ?? '-',
								},
								{
									title: 'FRM',
									dataIndex: 'frmName',
									render: (value) => value ?? '-',
								},
								{
									title: 'Limit Pembiayaan Existing',
									dataIndex: 'existingFinancingLimitAmount',
									align: 'right',
									render: (value) => formatCurrency(value ?? 0),
								},
								{
									title: 'Sifat Limit',
									dataIndex: 'financingLimitType',
									render: (value) => value ?? '-',
								},
								{
									title: 'Jenis Limit Pembiayaan',
									dataIndex: 'plafonType',
									render: (value) => value ?? '-',
								},
								{
									title: 'Expired Date',
									dataIndex: 'expiredDate',
									render: (value) => (value ? formatDate(value) : '-'),
								},
								{
									title: 'Sumber Order',
									dataIndex: 'orderSource',
									render: (value) => value ?? '-',
								},
								{
									title: 'Status Covenant',
									dataIndex: 'covenantStatus',
									render: (value) => value?.replaceAll(';', ', ') ?? '-',
								},
								{
									title: 'Detail Covenant',
									fixed: 'right',
									align: 'center',
									width: 120,
									render: (_, record) => (
										<Button
											type="link"
											onClick={() =>
												navigate('MonitoringCovenantPage', {
													resolutionId: record.resolutionId,
													spfId: record.spfId ?? undefined,
												})
											}
										>
											Detail
										</Button>
									),
								},
							]
				}
			/>
		</Fragment>
	);
};

export default MonitoringCovenantCustomerPage;
